import { useState } from "react";
import { Check, ExternalLink } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { getCheckoutUrl, getPortalUrl } from "@/lib/billing-links";
import type { CheckoutPlan } from "@/lib/billing-links";
import { LOGO_URL } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { useBillingStatusQuery } from "@/hooks/use-billing";
import { useUserQuery } from "@/hooks/use-user";
import { useAuthStore } from "@/stores/auth-store";

type PlanId = "go" | "business" | "enterprise";

function isCheckoutPlan(id: PlanId): id is CheckoutPlan {
  return id !== "enterprise";
}

interface PlanDefinition {
  id: PlanId;
  name: string;
  monthlyPrice?: number;
  annualPrice?: number;
  tagline: string;
  features: string[];
  highlighted?: boolean;
  contactHref?: string;
  // Fills the price slot when there is no numeric price, keeping cards aligned.
  priceNote?: string;
}

const PLANS: PlanDefinition[] = [
  {
    id: "go",
    name: "Go",
    monthlyPrice: 9,
    annualPrice: 90,
    tagline: "For individual makers shipping their first app.",
    features: [
      "1 service",
      "Push notifications",
      "Basic widgets",
      "Community support",
    ],
  },
  {
    id: "business",
    name: "Business",
    monthlyPrice: 49,
    annualPrice: 490,
    tagline: "For growing teams that need flexibility and scale.",
    features: [
      "Multiple services",
      "All widget templates",
      "Data source integrations",
      "Priority email support",
    ],
    highlighted: true,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    tagline: "For organisations with custom requirements.",
    features: [
      "Unlimited services",
      "Custom integrations",
      "Dedicated success manager",
      "SLA & advanced security",
    ],
    contactHref: "mailto:hi@appio.so?subject=Enterprise%20plan",
    priceNote: "Custom pricing tailored to your organisation's needs.",
  },
];

function formatPrice(amount: number): string {
  const hasCents = !Number.isInteger(amount);
  return `$${amount.toLocaleString("en-US", {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

export function PricingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [isAnnual, setIsAnnual] = useState(false);

  const billingStatusQuery = useBillingStatusQuery({ enabled: isAuthenticated });
  const { data: user } = useUserQuery({ enabled: isAuthenticated });

  const isSubscribed = billingStatusQuery.data?.entitled === true;

  const period = isAnnual ? "annual" : "monthly";
  // Only a signed-in, loaded, unsubscribed user is offered a Payment Link, so a
  // link counts as missing only when this user could otherwise have followed it.
  const checkoutUser = isAuthenticated && !isSubscribed ? user : undefined;
  const getPlanCheckoutUrl = (id: PlanId) =>
    checkoutUser && isCheckoutPlan(id)
      ? getCheckoutUrl(id, period, checkoutUser)
      : null;
  const isCheckoutMisconfigured =
    checkoutUser !== undefined &&
    PLANS.some(
      (plan) => isCheckoutPlan(plan.id) && getPlanCheckoutUrl(plan.id) === null
    );
  const portalUrl = user ? getPortalUrl(user) : null;

  return (
    <div className="pricing-page min-h-screen bg-muted/50 px-4 py-12 sm:py-16">
      <div className="mx-auto flex max-w-6xl flex-col items-center">
        <img
          src={LOGO_URL}
          alt=""
          className="mb-4 h-12 w-auto"
        />
        <h1 className="text-center text-3xl font-semibold tracking-tight sm:text-4xl">
          Choose your plan
        </h1>

        <div
          role="group"
          aria-label="Billing period"
          className="mt-8 flex w-full max-w-md items-center gap-4"
        >
          <button
            type="button"
            onClick={() => setIsAnnual(false)}
            className={cn(
              "flex-1 cursor-pointer whitespace-nowrap px-3 py-2.5 -mx-3 -my-2.5 text-right text-lg font-medium transition hover:opacity-70",
              isAnnual ? "text-muted-foreground" : "text-foreground"
            )}
          >
            Monthly
          </button>
          <button
            type="button"
            role="switch"
            aria-checked={isAnnual}
            aria-label="Toggle annual billing"
            onClick={() => setIsAnnual((prev) => !prev)}
            className={cn(
              "relative inline-flex h-8 w-14 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
              isAnnual
                ? "bg-primary hover:bg-primary/90"
                : "bg-input hover:bg-input/80"
            )}
          >
            <span
              className={cn(
                "pointer-events-none block h-6 w-6 rounded-full bg-background shadow-lg ring-0 transition-transform",
                isAnnual ? "translate-x-7" : "translate-x-0"
              )}
            />
          </button>
          <button
            type="button"
            onClick={() => setIsAnnual(true)}
            className={cn(
              "flex-1 cursor-pointer whitespace-nowrap px-3 py-2.5 -mx-3 -my-2.5 text-left text-lg font-medium transition hover:opacity-70",
              isAnnual ? "text-foreground" : "text-muted-foreground"
            )}
          >
            Annual
            <span className="pl-2 text-sm font-normal text-muted-foreground">
              2 months free
            </span>
          </button>
        </div>

        {isCheckoutMisconfigured && (
          <p role="alert" className="mt-6 text-base text-destructive">
            Checkout is not configured.
          </p>
        )}

        <div className="mt-12 grid w-full grid-cols-1 gap-6 md:grid-cols-3">
          {PLANS.map((plan) => {
            const { monthlyPrice, annualPrice } = plan;
            const checkoutUrl = getPlanCheckoutUrl(plan.id);

            return (
              <Card
                key={plan.id}
                className={cn(
                  "relative flex flex-col",
                  plan.highlighted && "ring-2 ring-primary"
                )}
              >
                {plan.highlighted && (
                  <Badge
                    variant="default"
                    className="absolute -top-3 left-1/2 -translate-x-1/2"
                  >
                    Most popular
                  </Badge>
                )}
                <CardHeader>
                  <CardTitle>{plan.name}</CardTitle>
                  <CardDescription>{plan.tagline}</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-1 flex-col gap-6">
                  <div className="flex min-h-[4.5rem] items-center">
                    {monthlyPrice != null && annualPrice != null ? (
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-baseline gap-2">
                          <span className="text-4xl font-semibold tracking-tight">
                            {formatPrice(isAnnual ? annualPrice : monthlyPrice)}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {isAnnual ? "per year" : "per month"}
                          </span>
                        </div>
                        <div className={cn(!isAnnual && "invisible")}>
                          <Badge
                            variant="success"
                            className="whitespace-nowrap"
                          >
                            2 months free
                          </Badge>
                        </div>
                      </div>
                    ) : (
                      plan.priceNote && (
                        <p className="text-[1.375rem] text-muted-foreground">
                          {plan.priceNote}
                        </p>
                      )
                    )}
                  </div>
                  {plan.contactHref ? (
                    <Button
                      asChild
                      variant={plan.highlighted ? "default" : "outline"}
                      className="w-full text-base"
                    >
                      <a href={plan.contactHref}>Contact us</a>
                    </Button>
                  ) : checkoutUrl ? (
                    <Button
                      asChild
                      variant={plan.highlighted ? "default" : "outline"}
                      className="w-full text-base"
                    >
                      <a href={checkoutUrl}>Subscribe</a>
                    </Button>
                  ) : (
                    // A real disabled button, not an anchor: a disabled
                    // <Button asChild> does not disable the <a> inside it.
                    // Subscribed customers change plans via the portal link
                    // below instead of starting a second checkout.
                    <Button
                      onClick={
                        isAuthenticated ? undefined : () => navigate("/login")
                      }
                      disabled={isAuthenticated}
                      variant={plan.highlighted ? "default" : "outline"}
                      className="w-full text-base"
                    >
                      Subscribe
                    </Button>
                  )}
                  <ul className="flex flex-col gap-2.5">
                    {plan.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-start gap-2 text-sm"
                      >
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {isAuthenticated && isSubscribed && portalUrl && (
          <div className="mt-10 flex flex-col items-center gap-2">
            <Button variant="link" asChild>
              <a href={portalUrl}>
                Manage your subscription
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
            <p className="text-sm text-muted-foreground">
              Sign in with the email you subscribed with.
            </p>
          </div>
        )}

        <Link
          to="/"
          className="mt-12 inline-block py-2 text-sm text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
        >
          Return to home page
        </Link>
      </div>
    </div>
  );
}
